import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveTypeApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { Flag, Edit2, Plus, X, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';

export default function LeaveTypesPage() {
  const queryClient = useQueryClient();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);

  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const { data: leaveTypes = [], isLoading } = useQuery({
    queryKey: ['leave-types'],
    queryFn: async () => {
      const res = await leaveTypeApi.getAll();
      const raw = res.data?.data ?? res.data;
      return Array.isArray(raw) ? raw : [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => editingType ? leaveTypeApi.update(editingType.id, data) : leaveTypeApi.create(data),
    onSuccess: () => {
      toast.success(`Leave type ${editingType ? 'updated' : 'created'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['leave-types'] });
      setIsPanelOpen(false);
      setEditingType(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to save leave type');
    }
  });

  const handleEdit = (type: any) => { setEditingType(type); setIsPanelOpen(true); };
  const handleAddNew = () => { setEditingType(null); setIsPanelOpen(true); };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name'),
      code: formData.get('code'),
      description: formData.get('description'),
      colour: formData.get('colour'),
      icon: formData.get('icon'),
      annualQuota: Number(formData.get('annualQuota')),
      maxCarryOver: Number(formData.get('maxCarryOver')),
      accrualType: formData.get('accrualType'),
      isEncashable: formData.get('isEncashable') === 'on',
      allowNegativeBalance: formData.get('allowNegativeBalance') === 'on',
      requiresMDSignoff: formData.get('requiresMDSignoff') === 'on',
      isActive: formData.get('isActive') === 'on',
      minNoticeDays: Number(formData.get('minNoticeDays')),
      maxConsecutiveDays: Number(formData.get('maxConsecutiveDays')),
      requiresDocAfterDays: Number(formData.get('requiresDocAfterDays')),
    };
    saveMutation.mutate(data);
  };

  // Shared colour tokens
  const borderCol  = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
  const panelBg    = isDark ? '#151f2e' : '#ffffff';
  const headerBg   = isDark ? '#0f172a'  : '#f8fafc';
  const hoverBg    = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-end">
        <Button onClick={handleAddNew}>
          <Plus className="w-4 h-4 mr-2" /> Add Leave Type
        </Button>
      </div>

      {/* Table card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--card-bg, #ffffff)',
          border: `1px solid var(--card-border, #e2e8f0)`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            {/* Table head */}
            <thead style={{
              backgroundColor: isDark ? '#131f31' : '#f8fafc',
              borderBottom: `1px solid ${borderCol}`,
            }}>
              <tr>
                {['Leave Type', 'Code', 'Quota', 'Carry Over', 'Encashable', 'MD Sign-off', 'Status', 'Actions'].map((col, i) => (
                  <th
                    key={col}
                    className={`px-6 py-4 text-xs font-semibold uppercase tracking-wider ${i === 4 || i === 5 || i === 6 ? 'text-center' : ''} ${i === 7 ? 'text-right' : ''}`}
                    style={{ color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table body */}
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-4">
                    <div className="h-20 animate-pulse rounded-lg" style={{ backgroundColor: isDark ? '#1e293b' : '#f1f5f9' }} />
                  </td>
                </tr>
              ) : leaveTypes.map((type: any) => (
                <tr
                  key={type.id}
                  className="transition-colors"
                  style={{ borderBottom: `1px solid ${borderCol}` }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = hoverBg)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {/* Leave Type name + icon */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: `${type.colour}25`,
                          color: type.colour,
                          border: `1px solid ${type.colour}40`,
                        }}
                      >
                        <Flag className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{type.name}</p>
                        <p className="text-xs mt-0.5 truncate max-w-[200px]" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
                          {type.description}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Code */}
                  <td className="px-6 py-4">
                    <span
                      className="text-xs font-bold px-2 py-1 rounded-lg"
                      style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                        color: isDark ? '#94a3b8' : '#475569',
                      }}
                    >
                      {type.code}
                    </span>
                  </td>

                  {/* Quota */}
                  <td className="px-6 py-4 font-medium" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                    {type.annualQuota} days
                  </td>

                  {/* Carry Over */}
                  <td className="px-6 py-4" style={{ color: isDark ? '#94a3b8' : '#475569' }}>
                    {type.maxCarryOver} days
                  </td>

                  {/* Encashable */}
                  <td className="px-6 py-4 text-center">
                    {type.isEncashable ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-lg"
                        style={{
                          backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : '#d1fae5',
                          color: isDark ? '#34d399' : '#065f46',
                        }}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Yes
                      </span>
                    ) : (
                      <span style={{ color: isDark ? '#334155' : '#cbd5e1' }}>—</span>
                    )}
                  </td>

                  {/* MD Sign-off */}
                  <td className="px-6 py-4 text-center">
                    {type.requiresMDSignoff ? (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-lg"
                        style={{
                          backgroundColor: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7',
                          color: isDark ? '#fbbf24' : '#92400e',
                          border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'}`,
                        }}
                      >
                        Req
                      </span>
                    ) : (
                      <span style={{ color: isDark ? '#334155' : '#cbd5e1' }}>—</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 text-center">
                    <span
                      className="text-xs font-bold px-2.5 py-1 rounded-lg"
                      style={type.isActive ? {
                        backgroundColor: isDark ? 'rgba(22,163,74,0.15)' : '#dcfce7',
                        color: isDark ? '#4ade80' : '#15803d',
                        border: `1px solid ${isDark ? 'rgba(22,163,74,0.3)' : '#bbf7d0'}`,
                      } : {
                        backgroundColor: isDark ? 'rgba(100,116,139,0.15)' : '#f1f5f9',
                        color: isDark ? '#64748b' : '#94a3b8',
                        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                      }}
                    >
                      {type.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(type)}>
                      <Edit2 className="w-4 h-4" style={{ color: isDark ? '#64748b' : '#94a3b8' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-out edit panel */}
      {isPanelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsPanelOpen(false)} />
          <div
            className="w-full max-w-md h-full shadow-2xl relative flex flex-col animate-slide-in-right"
            style={{ backgroundColor: panelBg, borderLeft: `1px solid ${borderCol}` }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ backgroundColor: headerBg, borderColor: borderCol }}
            >
              <h2 className="text-lg font-bold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                {editingType ? 'Edit Leave Type' : 'New Leave Type'}
              </h2>
              <button
                onClick={() => setIsPanelOpen(false)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: isDark ? '#64748b' : '#94a3b8' }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9';
                  (e.currentTarget as HTMLElement).style.color = isDark ? '#e2e8f0' : '#1e293b';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = isDark ? '#64748b' : '#94a3b8';
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel body */}
            <div className="flex-1 overflow-y-auto p-5">
              <form id="leaveTypeForm" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Name" name="name" defaultValue={editingType?.name} required />
                  <Input label="Code" name="code" defaultValue={editingType?.code} placeholder="e.g. AL, SL" required />
                </div>

                <Input label="Description" name="description" defaultValue={editingType?.description} />

                <div className="grid grid-cols-2 gap-4">
                  <Input label="Annual Quota (Days)" name="annualQuota" type="number" defaultValue={editingType?.annualQuota || 0} required />
                  <Input label="Max Carry Over (Days)" name="maxCarryOver" type="number" defaultValue={editingType?.maxCarryOver || 0} required />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Select label="Accrual Type" name="accrualType" defaultValue={editingType?.accrualType || 'YEARLY'}>
                    <option value="YEARLY">Yearly Upfront</option>
                    <option value="MONTHLY">Monthly Pro-rata</option>
                  </Select>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                      Theme Colour
                    </label>
                    <input
                      type="color"
                      name="colour"
                      defaultValue={editingType?.colour || '#3b82f6'}
                      className="h-10 w-full rounded-lg cursor-pointer p-1"
                      style={{ border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#0f172a' : '#ffffff' }}
                    />
                  </div>
                </div>

                {/* Policy Rules box */}
                <div
                  className="p-4 rounded-xl border space-y-4"
                  style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', borderColor: isDark ? '#334155' : '#e2e8f0' }}
                >
                  <h3 className="text-sm font-bold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Policy Rules</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Min Notice (Days)" name="minNoticeDays" type="number" defaultValue={editingType?.minNoticeDays || 0} />
                    <Input label="Max Consecutive (Days)" name="maxConsecutiveDays" type="number" defaultValue={editingType?.maxConsecutiveDays || 0} />
                  </div>
                  <Input label="Requires Doc After (Days)" name="requiresDocAfterDays" type="number" defaultValue={editingType?.requiresDocAfterDays || 0} helperText="0 means never required" />
                </div>

                {/* Toggles box */}
                <div
                  className="p-4 rounded-xl border space-y-3"
                  style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', borderColor: isDark ? '#334155' : '#e2e8f0' }}
                >
                  <h3 className="text-sm font-bold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Toggles</h3>

                  {[
                    { name: 'isEncashable', label: 'Encashable at year end', defaultChecked: editingType?.isEncashable, accent: 'blue' },
                    { name: 'allowNegativeBalance', label: 'Allow negative balance', defaultChecked: editingType?.allowNegativeBalance, accent: 'blue' },
                  ].map(toggle => (
                    <label key={toggle.name} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                      <input
                        type="checkbox"
                        name={toggle.name}
                        defaultChecked={toggle.defaultChecked}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      {toggle.label}
                    </label>
                  ))}

                  <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                    <input
                      type="checkbox"
                      name="requiresMDSignoff"
                      defaultChecked={editingType?.requiresMDSignoff}
                      className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="flex items-center gap-1">
                      Requires MD Sign-off <AlertCircle className="w-3 h-3 text-amber-500" />
                    </span>
                  </label>

                  <div className="pt-2 mt-2 border-t" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        defaultChecked={editingType ? editingType.isActive : true}
                        className="rounded border-slate-300 text-green-600 focus:ring-green-500"
                      />
                      Status: Active
                    </label>
                  </div>
                </div>
              </form>
            </div>

            {/* Panel footer */}
            <div
              className="px-5 py-4 flex justify-end gap-3 border-t"
              style={{ borderColor: borderCol, backgroundColor: headerBg }}
            >
              <Button variant="secondary" onClick={() => setIsPanelOpen(false)}>Cancel</Button>
              <Button type="submit" form="leaveTypeForm" isLoading={saveMutation.isPending}>
                <Save className="w-4 h-4 mr-2" /> Save Leave Type
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
